import React, {useState} from 'react';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import {useServerAlertRuleToggle} from 'features/server/model/useServerAlertRuleMutations';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import MoreActionsDots from 'shared/ui/MoreActions/MoreActionsDots';
import MoreActionsItem from 'shared/ui/MoreActions/MoreActionsItem';
import Edit from 'shared/ui/icons/General/Edit';
import Delete from 'shared/ui/icons/General/Delete';
import IServerAlertRule from 'features/server/model/ServerAlertRule/IServerAlertRule';
import {IServerAlertRuleMetricOption} from 'features/server/model/ServerAlertRule/IServerAlertRuleRecommendations';
import {secondsToRoundedMinutes} from 'shared/lib/datetime';
import {
    getServerAlertRuleMetricLabel,
    getServerAlertRuleSeverityLabel,
} from 'features/server/lib/serverAlertRuleHelper';

import ServerAlertRuleModal from '../ServerAlertRuleModal';
import ServerAlertRuleDeleteModal from '../ServerAlertRuleDeleteModal/ServerAlertRuleDeleteModal';

import styles from './ServerAlertRuleRow.module.scss';

interface IServerAlertRuleRowProps {
    item: IServerAlertRule,
    metricOptions: IServerAlertRuleMetricOption[],
    isCommon?: boolean,
}

type ModalType = 'edit' | 'delete' | null;

const ServerAlertRuleRow: React.FC<IServerAlertRuleRowProps> = ({item, metricOptions, isCommon = false}) => {
    const [activeModal, setActiveModal] = useState<ModalType>(null);
    const {toggle: onToggleEnabled} = useServerAlertRuleToggle(item);

    const handleCloseModal = () => setActiveModal(null);

    const metricLabel = getServerAlertRuleMetricLabel(metricOptions, item.metric);

    return (
        <>
            <tr className={isCommon ? styles.commonRow : styles.ownRow}>
                <td>
                    {metricLabel}
                    {item.scope && <span className={styles.scope}> ({item.scope})</span>}
                </td>
                <td>{item.operator} {item.threshold}</td>
                <td>{secondsToRoundedMinutes(item.for_seconds)} {t`мин.`}</td>
                <td>{secondsToRoundedMinutes(item.recover_seconds ?? item.for_seconds)} {t`мин.`}</td>
                <td>
                    <span className={cn(styles.severity, styles[item.severity])}>
                        {getServerAlertRuleSeverityLabel(item.severity)}
                    </span>
                </td>
                <td>
                    <StatusToggle
                        condition={item.enabled}
                        onClick={onToggleEnabled}
                        textActive={t`Включено`}
                        notActiveText={t`Выключено`}
                    />
                </td>
                <td>
                    <div className="d-flex justify-content-center">
                        <MoreActionsDots placement="auto">
                            <MoreActionsItem
                                label={t`Редактировать`}
                                icon={<Edit/>}
                                onClick={() => setActiveModal('edit')}
                                data-modal-trigger="true"
                            />
                            <MoreActionsItem
                                label={t`Удалить`}
                                icon={<Delete/>}
                                onClick={() => setActiveModal('delete')}
                                data-modal-trigger="true"
                            />
                        </MoreActionsDots>
                    </div>
                </td>
            </tr>

            <ServerAlertRuleModal
                item={item}
                open={activeModal === 'edit'}
                handleClose={handleCloseModal}
            />

            <ServerAlertRuleDeleteModal
                item={item}
                open={activeModal === 'delete'}
                handleClose={handleCloseModal}
            />
        </>
    );
};

export default ServerAlertRuleRow;
